import { Component, ViewChild, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { GoogleMapComponent } from '@shared/components';
import { MapHelperService, TripActualLocationService } from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import type { TDealership, TTrip, TTripActualLocationDoc } from '@shared/models';
import { Constants } from '@shared/models';

@Component({
    'selector': 'app-trackerHistoricalRoutes',
    'templateUrl': './tracker3HistoricalRoutes.page.html',
    'styleUrls': ['./tracker3HistoricalRoutes.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule, GoogleMapComponent ]
})
export class Tracker3HistoricalRoutesPage extends ViewBasePage implements OnInit, OnDestroy {
    @ViewChild(GoogleMapComponent) public mapComponent!: GoogleMapComponent;
    
    public vm: any = {};
    public dealershipCoordinate: { latitude: number; longitude: number; } = { latitude: 0, longitude: 0 };
    public currentTrip!: TTrip;
    public currentTripIdx = 0;
    
    private dealership!: TDealership;
    private polylines: google.maps.Polyline[] = [];

    constructor(private mapHelperService: MapHelperService, 
        private tripActualLocationService: TripActualLocationService, injector: Injector) {
        super(injector);
        super.setCurrentView(this.Constants.VIEW_ROUTE_PAGE5);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();

        this.vm = super.getViewModel();
        if (!this.vm?.tripData) {
            return;
        }
        this.currentTripIdx = 0;
        this.currentTrip = this.vm.tripData.trips[this.currentTripIdx];

        try {
            this.dealership = this.sessionService.getDealership();
            this.dealershipCoordinate = {
                latitude: this.dealership.address.coordinate.latitude,
                longitude: this.dealership.address.coordinate.longitude
            };

            this.initMoreMenu();
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public initMoreMenu(): void {
        this.resetPopoverMenu();
        this.pushPopoverMenuItem('Show Tour', 'show-tracker-historical-3-tour');
    }

    public getNumTrips(): number {
        const tripData = this.vm.tripData;

        return (tripData) ? tripData.trips.length : 0;
    }

    public onCenterDealershipClick(): void {
        this.mapComponent.centerMap(this.dealershipCoordinate);
    }

    public getTripStartDate(trip: TTrip): string {
        let str = '';

        if (trip) {
            const timestamp = trip.startTripDate;
            str = this.dateService.formatTimestamp(timestamp, Constants.DateTimeFormats.All);
        }
        return str;
    }


    public getTripDate(trip: TTrip): string {
        let str = '';

        if (trip) {
            const timestamp = trip.startTripDate;
            str = this.dateService.formatTimestamp(timestamp, Constants.DateTimeFormats.LongDate);
        }
        return str;
    }

    public getStartTime(trip: TTrip): string {
        let str = '';

        if (trip) {
            const timestamp = trip.startTripDate; // asdf
            str = this.dateService.formatTimestamp(timestamp, Constants.DateTimeFormats.ShortTime);
        }
        return str;
    }

    public getEndTime(trip: TTrip): string {
        let str = '';

        if (trip) {
            const timestamp = trip.endTripDate;
            str = this.dateService.formatTimestamp(timestamp, Constants.DateTimeFormats.ShortTime);
        }
        return str;
    }

    public getDuration(trip: TTrip): string {
        let str = '';

        if (trip) {
            const timestamp1 = trip.startTripDate;
            const timestamp2 = trip.endTripDate;
            str = this.dateService.formatDuration(timestamp1, timestamp2);
        }
        return str;
    }

    public getTripType(trip: TTrip): string {
        return trip?.type;
    }

    public getDriver(trip: TTrip): string {
        let str = '';

        if (trip) {
            str = trip.driver;
        }
        return str;
    }

    /**
     * Handle map ready event - add dealership marker and draw initial trip
     */
    public async onMapReady(): Promise<void> {
        await this.uxHelperService.showSpinner('Loading map.');

        try {
            await this.createDealershipMarker();
            await this.drawTrip(this.currentTripIdx);
            await this.uxHelperService.hideSpinner();
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    /**
     * Handle map error event
     */
    public onMapError(event: { message: string; error?: unknown }): void {
        // console.error('Map error:', event);
        this.uxHelperService.showAlert('Map Error', event.message);
    }

    /**
     * Create dealership marker with star icon
     */
    private async createDealershipMarker(): Promise<void> {
        const dealershipName = this.dealership.dealershipName;
        const iconUrl = this.mapHelperService.getMarkerIcon('Dealership');

        this.mapComponent.addDealershipMarker(
            this.dealershipCoordinate,
            dealershipName,
            iconUrl
        );
    }

    public onPreviousTripClick() {
        const len = this.vm.tripData.trips.length;
        this.currentTripIdx = (this.currentTripIdx === 0) ? (len - 1) : --this.currentTripIdx;
        this.currentTrip = this.vm.tripData.trips[this.currentTripIdx];

        // for the moment, next will only do the draw and previous will only do the erase
        this.drawTrip(this.currentTripIdx);
    }

    public onNextTripClick() {
        const len = this.vm.tripData.trips.length;
        this.currentTripIdx = (this.currentTripIdx === len - 1) ? 0 : ++this.currentTripIdx;
        this.currentTrip = this.vm.tripData.trips[this.currentTripIdx];

        this.drawTrip(this.currentTripIdx);
        // this.drawActualRoute();
    }

    private async drawTrip(tripIdx: number): Promise<void> {
        // Clear existing routes and markers
        this.mapComponent.clearRoutes();
        this.mapComponent.clearMarkers();
        
        // Re-add dealership marker
        await this.createDealershipMarker();
        
        // Add trip markers
        this.createStartAndEndMarkers();
        this.createDestinationMarkers();
        
        // Draw actual shuttle path
        await this.showActualShuttlePath();
    }

    /**
     * Create start and end markers for the trip
     */
    private createStartAndEndMarkers(): void {
        const startingPoint = this.currentTrip.plannedRoute.startingPoint;
        const endingPoint = this.currentTrip.plannedRoute.endingPoint;

        // Determine if same address
        const isSameAddress = (
            startingPoint.location.address.streetAddress ===
            endingPoint.location.address.streetAddress
        );

        // Get appropriate icons
        const startIcon = isSameAddress
            ? this.mapHelperService.getMarkerIcon('startNW')
            : this.mapHelperService.getMarkerIcon('startNS');

        const endIcon = isSameAddress
            ? this.mapHelperService.getMarkerIcon('endNE')
            : this.mapHelperService.getMarkerIcon('endNS');

        // Add start marker
        this.mapComponent.addStartMarker(
            {
                latitude: startingPoint.location.address.coordinate.latitude,
                longitude: startingPoint.location.address.coordinate.longitude
            },
            startingPoint.location.locationName,
            startingPoint.location.address.streetAddress,
            startIcon
        );

        // Add end marker
        this.mapComponent.addEndMarker(
            {
                latitude: endingPoint.location.address.coordinate.latitude,
                longitude: endingPoint.location.address.coordinate.longitude
            },
            endingPoint.location.locationName,
            endingPoint.location.address.streetAddress,
            endIcon
        );
    }

    /**
     * Create destination markers with letter labels (A, B, C, etc.)
     */
    private createDestinationMarkers(): void {
        const routeLegs = this.currentTrip.plannedRoute.routeLegs;

        for (let idx = 0; idx < routeLegs.length; idx++) {
            const leg = routeLegs[idx];

            // Skip special markers (start/end have their own icons)
            if (leg.letter !== '?') {
                const coordinate = {
                    latitude: leg.to.location.address.coordinate.latitude,
                    longitude: leg.to.location.address.coordinate.longitude
                };

                const title = `${leg.passengerName}. ${leg.to.location.address.streetAddress}`;

                this.mapComponent.addLetterMarker(leg.letter, coordinate, title);
            }
        }
    }

    /**
     * Draw actual shuttle path from historical location data
     * Uses alternating colors (red/blue) for different legs
     */
    private async showActualShuttlePath(): Promise<void> {
        const legColors = ['#ff0000', '#0000ff']; // red, blue
        const actualLocations = await this.tripActualLocationService.getAll(this.currentTrip.uid);
        const numLegs = Math.max(...actualLocations.map(o => o.legIndex)) + 1;

        for (let leg = 0; leg < numLegs; ++leg) {
            // Get the actual locations for this leg
            const legLocations = actualLocations.filter(location => (location.legIndex === leg));
            const sortedLegLocations = this.sort(legLocations);

            // Transform our latitude & longitude into coordinate array
            const path: { latitude: number; longitude: number }[] = [];
            sortedLegLocations.forEach(location => {
                const actualPositions = location.actualPositions;
                actualPositions.forEach(position => {
                    path.push({
                        latitude: position.coordinate.latitude,
                        longitude: position.coordinate.longitude
                    });
                });
            });

            // Alternate colors between legs
            const legColor = legColors[leg % 2];

            // Draw polyline using GoogleMapComponent
            this.mapComponent.drawPolyline({
                path: path,
                color: legColor,
                weight: 3,
                opacity: 1.0
            });
        }
    }

    // Move this to MapHelper
    public sort(locations: TTripActualLocationDoc[]): TTripActualLocationDoc[] {
        const sortedLocations = locations.sort((a, b) => {
            const aSequenceNum = a.sequenceNum;
            const bSequenceNum = b.sequenceNum;

            if (aSequenceNum < bSequenceNum) {
                return -1;
            } else if (aSequenceNum > bSequenceNum) {
                return 1;
            } else {
                return 0;
            }
        });
        return sortedLocations;
    }
}
